import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PagesSidebar from '../components/PagesSidebar';
import BlockTree from '../components/BlockTree';
import Preview from '../components/Preview';
import AddBlockMenu from '../components/AddBlockMenu';
import InspectorTabs from '../components/InspectorTabs';
import CodePanelLazy from '../components/CodePanelLazy';
import PageSettingsModal from '../components/PageSettingsModal';
import MediaBrowser from '../components/MediaBrowser';
import { api, ApiException } from '../api/client';
const RAIL_TREE_KEY = 'ssv2:editor:railTree';
const RAIL_INSPECTOR_KEY = 'ssv2:editor:railInspector';
/**
 * Phase 3/4 Editor model:
 *   savedPage / savedContent / etag — last server-acknowledged state.
 *   draftPage / draftContent / tombedIds — current in-memory edits.
 *   dirty := draftPage !== savedPage || draftContent diverges from savedContent.
 *
 * Mutations update the draft only and post a real-time patch to the iframe
 * via the Preview ref (preview-helper.js applies it). The iframe is loaded
 * once per slug and only reloaded on Save / Undo / Redo / Discard / slug change.
 */
export default function Editor({ config, brand }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    // ---------- saved state (last sync point) ----------
    const [savedPage, setSavedPage] = useState(null);
    const [savedContent, setSavedContent] = useState({});
    const [etag, setEtag] = useState('');
    // ---------- in-memory draft ----------
    const [draftPage, setDraftPage] = useState(null);
    const [draftContent, setDraftContent] = useState({});
    const [tombedIds, setTombedIds] = useState([]);
    // ---------- ui state ----------
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busySave, setBusySave] = useState(false);
    const [busyMutation, setBusyMutation] = useState(false);
    const [conflict, setConflict] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [previewKey] = useState(0);
    const [pageCode, setPageCode] = useState(null);
    const [blockCode, setBlockCode] = useState(null);
    const [showPageSettings, setShowPageSettings] = useState(false);
    const [pagesRefreshKey, setPagesRefreshKey] = useState(0);
    const [editMode, setEditMode] = useState(true); // Phase 3: always-edit by default
    const [inlineEditingBlockId, setInlineEditingBlockId] = useState(null);
    const [mediaPicker, setMediaPicker] = useState(null);
    const [inlineToast, setInlineToast] = useState(null);
    const [forceInspectorTab, setForceInspectorTab] = useState(null);
    const previewRef = useRef(null);
    const previewHandle = useRef(null);
    const pendingRenders = useRef(new Set());
    const renderTimer = useRef(null);
    // Mirror draft + saved state in refs so the *scheduled* render flusher
    // always reads the latest values, not the closure that was current when
    // setTimeout was registered. Without this, fast Inspector edits or media
    // swaps render the iframe against pre-edit data and look like nothing
    // changed.
    const draftPageRef = useRef(null);
    const draftContentRef = useRef({});
    const savedContentRef = useRef({});
    useEffect(() => { draftPageRef.current = draftPage; }, [draftPage]);
    useEffect(() => { draftContentRef.current = draftContent; }, [draftContent]);
    useEffect(() => { savedContentRef.current = savedContent; }, [savedContent]);
    const [treeOpen, setTreeOpen] = useState(() => loadRail(RAIL_TREE_KEY, true));
    const [inspectorOpen, setInspectorOpen] = useState(() => loadRail(RAIL_INSPECTOR_KEY, true));
    function toggleTree() { setTreeOpen((v) => { saveRail(RAIL_TREE_KEY, !v); return !v; }); }
    function toggleInspector() { setInspectorOpen((v) => { saveRail(RAIL_INSPECTOR_KEY, !v); return !v; }); }
    // ---------- load page ----------
    useEffect(() => {
        if (!slug) {
            setSavedPage(null);
            setDraftPage(null);
            setSavedContent({});
            setDraftContent({});
            setTombedIds([]);
            setSelectedId(null);
            setEtag('');
            return;
        }
        let alive = true;
        setLoading(true);
        setError(null);
        api.getPage(slug)
            .then(({ page, etag: e, content_map }) => {
            if (!alive)
                return;
            setSavedPage(page);
            setDraftPage(page);
            setSavedContent(content_map ?? {});
            setDraftContent({});
            setTombedIds([]);
            setEtag(e ?? '');
            setSelectedId((cur) => cur && findBlock(page.blocks, cur) ? cur : page.blocks[0]?.id ?? null);
        })
            .catch((e) => {
            if (!alive)
                return;
            if (e instanceof ApiException && e.code === 'PAGE_NOT_FOUND')
                navigate('/pages', { replace: true });
            else
                setError(e instanceof ApiException ? e.message : 'Failed to load page');
        })
            .finally(() => { if (alive)
            setLoading(false); });
        return () => { alive = false; };
    }, [slug, navigate]);
    // ---------- dirty + dirty count ----------
    const dirty = useMemo(() => {
        if (!savedPage || !draftPage)
            return false;
        if (JSON.stringify(savedPage) !== JSON.stringify(draftPage))
            return true;
        for (const [id, html] of Object.entries(draftContent)) {
            if ((savedContent[id] ?? '') !== html)
                return true;
        }
        if (tombedIds.length > 0)
            return true;
        return false;
    }, [savedPage, draftPage, savedContent, draftContent, tombedIds]);
    // ---------- beforeunload guard ----------
    useEffect(() => {
        function onBeforeUnload(e) {
            if (!dirty)
                return;
            e.preventDefault();
            e.returnValue = '';
        }
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);
    // ---------- helpers: read content for a block (draft → saved) ----------
    // Closure-aware version for synchronous handlers that run during the same
    // render that owns the latest state. Async/scheduled callers must use the
    // ref-backed `contentForLatest`.
    function contentFor(blockId) {
        if (Object.prototype.hasOwnProperty.call(draftContent, blockId))
            return draftContent[blockId];
        return savedContent[blockId];
    }
    function contentForLatest(blockId) {
        if (Object.prototype.hasOwnProperty.call(draftContentRef.current, blockId))
            return draftContentRef.current[blockId];
        return savedContentRef.current[blockId];
    }
    // ---------- helpers: schedule render-block for a changed block ----------
    function scheduleBlockPatch(blockId) {
        pendingRenders.current.add(blockId);
        if (renderTimer.current !== null)
            return;
        renderTimer.current = window.setTimeout(flushBlockPatches, 120);
    }
    async function flushBlockPatches() {
        renderTimer.current = null;
        const ids = Array.from(pendingRenders.current);
        pendingRenders.current.clear();
        // Read the latest state from refs — the closure that registered this
        // timer captured the pre-edit `draftPage` / `draftContent`. Using refs
        // is what makes setting/media changes visibly update the iframe.
        const dp = draftPageRef.current;
        if (!slug || !dp)
            return;
        for (const id of ids) {
            const block = findBlock(dp.blocks, id);
            if (!block)
                continue;
            try {
                const content = block.content ? (contentForLatest(id) ?? '') : undefined;
                const r = await api.renderBlock(slug, block, content);
                previewHandle.current?.postPatch({
                    type: 'preview:replace-block',
                    id,
                    html: r.html,
                    scoped_css: r.scoped_css,
                    custom_js: r.custom_js,
                });
            }
            catch (e) {
                // Non-fatal: preview falls behind on patch errors. Reload escape hatch.
                console.warn('[ssv2] render-block failed for', id, e);
            }
        }
    }
    // ---------- mutations: all of these write to draft only ----------
    function commitDraftBlocks(updater) {
        setDraftPage((p) => p ? { ...p, blocks: updater(p.blocks) } : p);
    }
    /** Per-block settings change (called from Inspector on every keystroke). */
    function handleSettingsChange(blockId, settings) {
        commitDraftBlocks((blocks) => updateBlockInTree(blocks, blockId, (b) => ({ ...b, settings })));
        scheduleBlockPatch(blockId);
    }
    /** Per-block content change (Content tab textarea or rich-text editor). */
    function handleContentChange(blockId, html) {
        setDraftContent((m) => ({ ...m, [blockId]: html }));
        scheduleBlockPatch(blockId);
    }
    async function handleAdd(entry) {
        if (!draftPage || !slug)
            return;
        setBusyMutation(true);
        try {
            const targetParentId = resolveSmartParent(draftPage.blocks, selectedId, entry.type);
            const usedIds = collectIds(draftPage.blocks);
            const newBlock = makeNewBlock(entry.type, entry.layout, usedIds);
            const { blocks: nextBlocks, parent } = insertInTree(draftPage.blocks, targetParentId, newBlock);
            let extraInsertedIds = [];
            // Section auto-scaffold: container(fluid) > row(g-3) > col-12 inside.
            let finalBlocks = nextBlocks;
            let finalSelectId = newBlock.id;
            if (entry.type === 'section') {
                const u = collectIds(finalBlocks);
                const container = makeNewBlock('container', undefined, u, { size: 'fluid' });
                u.push(container.id);
                const row = makeNewBlock('row', undefined, u, { gap: 'g-3' });
                u.push(row.id);
                const col = makeNewBlock('column', undefined, u, { width: { xs: 12 } });
                u.push(col.id);
                // Build hierarchy then graft into the section's children.
                const sectionWithChildren = {
                    ...newBlock,
                    children: [{ ...container, children: [{ ...row, children: [col] }] }],
                };
                finalBlocks = updateBlockInTree(finalBlocks, newBlock.id, () => sectionWithChildren);
                extraInsertedIds = [container.id, row.id, col.id];
                finalSelectId = col.id;
            }
            setDraftPage({ ...draftPage, blocks: finalBlocks });
            setSelectedId(finalSelectId);
            // Inserts need full subtree HTML for the iframe — render parent if there
            // is one, otherwise use tree-insert with the new block's HTML.
            await scheduleInsertPatch(slug, finalBlocks, parent, newBlock.id, extraInsertedIds);
        }
        catch (e) {
            setError(e?.message ?? 'Insert failed');
        }
        finally {
            setBusyMutation(false);
        }
    }
    async function handleAddChild(parentId) {
        if (!draftPage || !slug)
            return;
        const parent = findBlock(draftPage.blocks, parentId);
        if (!parent)
            return;
        if (parent.type === 'column') {
            setSelectedId(parentId);
            return;
        }
        setBusyMutation(true);
        try {
            const usedIds = collectIds(draftPage.blocks);
            let newBlock;
            if (parent.type === 'section')
                newBlock = makeNewBlock('container', undefined, usedIds, { size: 'fluid' });
            else if (parent.type === 'container')
                newBlock = makeNewBlock('row', undefined, usedIds, { gap: 'g-3' });
            else if (parent.type === 'row')
                newBlock = makeNewBlock('column', undefined, usedIds, { width: { xs: 12 } });
            else
                return;
            const { blocks } = insertInTree(draftPage.blocks, parentId, newBlock);
            setDraftPage({ ...draftPage, blocks });
            setSelectedId(newBlock.id);
            await scheduleInsertPatch(slug, blocks, parentId, newBlock.id, []);
        }
        catch (e) {
            setError(e?.message ?? 'Insert failed');
        }
        finally {
            setBusyMutation(false);
        }
    }
    async function handleDuplicate(id) {
        if (!draftPage || !slug)
            return;
        setBusyMutation(true);
        try {
            const source = findBlock(draftPage.blocks, id);
            if (!source)
                throw new Error('Block not found');
            const usedIds = collectIds(draftPage.blocks);
            const oldToNew = {};
            const cloned = cloneSubtreeWithFreshIds(source, usedIds, oldToNew);
            // Carry over draft content for cloned content blocks.
            setDraftContent((m) => {
                const next = { ...m };
                for (const [oldId, newId] of Object.entries(oldToNew)) {
                    const html = contentFor(oldId);
                    if (html !== undefined)
                        next[newId] = html;
                    else
                        next[newId] = '';
                }
                return next;
            });
            const parent = findParentId(draftPage.blocks, id);
            const idx = indexOf(draftPage.blocks, id, parent) + 1;
            const blocks = insertAtIndex(draftPage.blocks, parent, idx, cloned);
            setDraftPage({ ...draftPage, blocks });
            setSelectedId(cloned.id);
            await scheduleInsertPatch(slug, blocks, parent, cloned.id, []);
        }
        catch (e) {
            setError(e?.message ?? 'Duplicate failed');
        }
        finally {
            setBusyMutation(false);
        }
    }
    async function handleDelete(id) {
        if (!draftPage || !slug)
            return;
        if (!window.confirm(`Delete block "${id}"? Children will also be removed.`))
            return;
        setBusyMutation(true);
        try {
            const removed = findBlock(draftPage.blocks, id);
            const blocks = removeFromTree(draftPage.blocks, id);
            setDraftPage({ ...draftPage, blocks });
            // Tomb the deleted block + any descendants that own content files.
            if (removed) {
                const ids = [];
                for (const b of flatten([removed]))
                    if (b.content)
                        ids.push(b.id);
                setTombedIds((cur) => Array.from(new Set([...cur, ...ids])));
            }
            // Patch iframe.
            previewHandle.current?.postPatch({ type: 'preview:tree-remove', id });
            if (selectedId === id)
                setSelectedId(null);
        }
        catch (e) {
            setError(e?.message ?? 'Delete failed');
        }
        finally {
            setBusyMutation(false);
        }
    }
    async function handleReorder(nextBlocks) {
        if (!draftPage || !slug)
            return;
        setDraftPage({ ...draftPage, blocks: nextBlocks });
        // After a tree reshuffle the iframe DOM is out of sync; reload it.
        // (Iframe DnD events come through inline:tree-drop and patch directly.)
        previewHandle.current?.reload();
    }
    // ---------- inline-edit message handler (text + media + tree-drop) ----------
    const handleInlineMessage = useCallback(async (msg) => {
        if (!slug || !draftPage)
            return;
        if (msg.type === 'inline:edit-start') {
            setInlineEditingBlockId(msg.block_id);
            setSelectedId(msg.block_id);
            return;
        }
        if (msg.type === 'inline:edit-cancel') {
            setInlineEditingBlockId(null);
            return;
        }
        if (msg.type === 'inline:open-source') {
            setInlineEditingBlockId(null);
            setSelectedId(msg.block_id);
            setForceInspectorTab({ tab: 'content', blockId: msg.block_id, nonce: Date.now() });
            return;
        }
        if (msg.type === 'inline:text-commit') {
            setInlineEditingBlockId(null);
            const source = contentFor(msg.block_id) ?? '';
            const { html: next, located } = applyTextEditToSource(source, {
                tag: msg.leaf.tag,
                ordinal: msg.leaf.ordinal,
                originalText: msg.leaf.original_text,
                newInnerHtml: sanitizeLeafHtml(msg.new_inner_html),
            });
            if (!located) {
                setInlineToast('Could not locate that text in the source. Use the Content tab to edit it directly.');
                return;
            }
            if (next === source) {
                setInlineToast('Nothing changed.');
                return;
            }
            handleContentChange(msg.block_id, next);
            setInlineToast('Edited (unsaved)');
            setTimeout(() => setInlineToast((t) => t === 'Edited (unsaved)' ? null : t), 1200);
            return;
        }
        if (msg.type === 'inline:add-child') {
            handleAddChild(msg.parent_id);
            return;
        }
        if (msg.type === 'inline:tree-drop') {
            const next = applyTreeDrop(draftPage.blocks, msg.src_id, msg.over_id, msg.position);
            if (!next) {
                setInlineToast('Could not move that block. Try the block tree on the left.');
                return;
            }
            setDraftPage({ ...draftPage, blocks: next });
            // Patch iframe to match.
            const parentId = findParentId(next, msg.src_id);
            const beforeId = nextSiblingId(next, parentId, msg.src_id);
            previewHandle.current?.postPatch({
                type: 'preview:tree-move',
                id: msg.src_id,
                parent_id: parentId,
                before_id: beforeId,
            });
            return;
        }
        if (msg.type === 'inline:open-media') {
            if (!Number.isInteger(msg.ordinal)) {
                setInlineToast('Stale iframe code detected — please hard-refresh (Ctrl+Shift+R).');
                return;
            }
            setMediaPicker({ blockId: msg.block_id, ordinal: msg.ordinal, kind: msg.kind });
            return;
        }
    }, [slug, draftPage, draftContent, savedContent]);
    async function handleMediaPickFromIframe(rel, _item) {
        if (!slug || !mediaPicker)
            return;
        const { blockId, ordinal } = mediaPicker;
        setMediaPicker(null);
        const source = contentFor(blockId) ?? '';
        const { html: next, located } = applyMediaSwapToSource(source, ordinal, rel);
        if (!located) {
            setInlineToast(`Could not find image #${ordinal + 1} in this block's source. Try the Content tab.`);
            return;
        }
        if (next === source) {
            setInlineToast('Image already points there.');
            return;
        }
        handleContentChange(blockId, next);
        setInlineToast('Image swapped (unsaved)');
        setTimeout(() => setInlineToast((t) => t === 'Image swapped (unsaved)' ? null : t), 1500);
    }
    // ---------- save / discard ----------
    async function handleSave() {
        if (!slug || !draftPage || !dirty)
            return;
        setBusySave(true);
        setError(null);
        try {
            // Build content_map: include any draft entry that differs from saved,
            // plus any new content block that has neither a draft nor a saved entry
            // (so the server creates an empty file for it).
            const contentMap = {};
            for (const [id, html] of Object.entries(draftContent)) {
                if ((savedContent[id] ?? '') !== html)
                    contentMap[id] = html;
            }
            for (const b of flatten(draftPage.blocks)) {
                if (b.content && !(b.id in savedContent) && !(b.id in contentMap)) {
                    contentMap[b.id] = '';
                }
            }
            const result = await api.savePage(slug, {
                page: draftPage,
                content_map: contentMap,
                deleted_content: tombedIds,
            }, etag);
            setSavedPage(result.page);
            setDraftPage(result.page);
            setSavedContent(result.content_map);
            setDraftContent({});
            setTombedIds([]);
            setEtag(result.etag);
            setInlineToast('Saved');
            setTimeout(() => setInlineToast((t) => t === 'Saved' ? null : t), 1200);
            // Reload iframe so server-rendered output reflects the canonical state.
            previewHandle.current?.reload();
        }
        catch (e) {
            if (e instanceof ApiException && e.status === 409) {
                const detail = e.extra ?? null;
                // The error response includes `current` via json_err extras — fish it
                // out of the error object if the helper preserved it.
                setConflict({
                    etag: detail?.current?.etag ?? '',
                    page: detail?.current?.page ?? draftPage,
                    content_map: detail?.current?.content_map ?? savedContent,
                });
                setError('Page changed since you started editing — choose Discard or Keep.');
            }
            else {
                setError(e?.message ?? 'Save failed');
            }
        }
        finally {
            setBusySave(false);
        }
    }
    function handleDiscard() {
        if (!savedPage)
            return;
        if (dirty && !window.confirm('Discard unsaved changes?'))
            return;
        setDraftPage(savedPage);
        setDraftContent({});
        setTombedIds([]);
        setError(null);
        setConflict(null);
        previewHandle.current?.reload();
    }
    // ---------- undo / redo ----------
    async function handleUndo() {
        if (!slug)
            return;
        if (dirty && !window.confirm('Undoing will discard your unsaved changes. Continue?'))
            return;
        setBusyMutation(true);
        try {
            const r = await api.undoPage(slug);
            setSavedPage(r.page);
            setDraftPage(r.page);
            setSavedContent(r.content_map ?? {});
            setDraftContent({});
            setTombedIds([]);
            setEtag(r.etag ?? '');
            previewHandle.current?.reload();
        }
        catch (e) {
            if (e?.code !== 'NO_REVISIONS')
                setError(e?.message ?? 'Undo failed');
        }
        finally {
            setBusyMutation(false);
        }
    }
    async function handleRedo() {
        if (!slug)
            return;
        if (dirty && !window.confirm('Redo will discard your unsaved changes. Continue?'))
            return;
        setBusyMutation(true);
        try {
            const r = await api.redoPage(slug);
            setSavedPage(r.page);
            setDraftPage(r.page);
            setSavedContent(r.content_map ?? {});
            setDraftContent({});
            setTombedIds([]);
            setEtag(r.etag ?? '');
            previewHandle.current?.reload();
        }
        catch (e) {
            if (e?.code !== 'NO_REDO')
                setError(e?.message ?? 'Redo failed');
        }
        finally {
            setBusyMutation(false);
        }
    }
    // ---------- keyboard shortcuts ----------
    useEffect(() => {
        function onKey(e) {
            const target = e.target;
            const tag = target?.tagName ?? '';
            const inField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || target?.isContentEditable;
            const mod = e.metaKey || e.ctrlKey;
            if (!mod)
                return;
            const k = e.key.toLowerCase();
            if (k === 's') {
                e.preventDefault();
                if (dirty)
                    handleSave();
                return;
            }
            if (inField)
                return;
            if (k === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            }
            else if ((k === 'z' && e.shiftKey) || k === 'y') {
                e.preventDefault();
                handleRedo();
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });
    // ---------- patch helper for newly-inserted blocks ----------
    async function scheduleInsertPatch(slug, blocks, parentId, newBlockId, _extraIds) {
        const newBlock = findBlock(blocks, newBlockId);
        if (!newBlock)
            return;
        try {
            // Brand-new content blocks have no file on disk — pass an explicit
            // empty content so block_content() doesn't emit the "missing" comment.
            const content = newBlock.content ? '' : undefined;
            const r = await api.renderBlock(slug, newBlock, content);
            const beforeId = nextSiblingId(blocks, parentId, newBlockId);
            previewHandle.current?.postPatch({
                type: 'preview:tree-insert',
                parent_id: parentId,
                before_id: beforeId,
                html: r.html,
                scoped_css: r.scoped_css,
                custom_js: r.custom_js,
            });
        }
        catch (e) {
            console.warn('[ssv2] insert render failed', e);
            previewHandle.current?.reload();
        }
    }
    // ---------- selected block (from draft tree) ----------
    const selected = useMemo(() => (draftPage && selectedId ? findBlock(draftPage.blocks, selectedId) : null), [draftPage, selectedId]);
    const workspaceCls = `editor-workspace${treeOpen ? '' : ' tree-collapsed'}${inspectorOpen ? '' : ' inspector-collapsed'}`;
    return (_jsxs("div", { className: "editor", children: [_jsx(PagesSidebar, { selectedSlug: slug, refreshKey: pagesRefreshKey }), _jsxs("div", { className: "editor-main", children: [!slug && _jsx("div", { className: "editor-empty", children: _jsx(EmptyHint, {}) }), slug && loading && _jsxs("div", { className: "muted pad", children: ["Loading ", slug, "\u2026"] }), slug && error && _jsx("div", { className: "err pad", children: error }), slug && draftPage && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "editor-toolbar", children: [_jsxs("div", { className: "editor-toolbar-left", children: [_jsxs("button", { className: "rail-toggle", onClick: toggleTree, title: treeOpen ? 'Hide block tree' : 'Show block tree', "aria-pressed": treeOpen, children: [treeOpen ? '◀' : '▶', " Blocks"] }), _jsxs("div", { className: "editor-toolbar-title", children: [_jsx("strong", { children: draftPage.title }), _jsxs("span", { className: "tag", title: "page slug", children: ["/", draftPage.slug === 'home' ? '' : draftPage.slug] }), _jsxs("span", { className: "muted small", children: [flatten(draftPage.blocks).length, " blocks"] }), dirty && _jsx("span", { className: "tag warn-tag", title: "Unsaved changes", children: "\u25CF Unsaved" })] })] }), _jsxs("div", { className: "editor-toolbar-right", children: [_jsx(AddBlockMenu, { onPick: handleAdd }), _jsx("button", { className: "ghost", onClick: () => setShowPageSettings(true), title: "Page meta + SEO", children: "\u2699" }), _jsx("button", { className: "ghost", onClick: async () => {
                                                    if (!slug)
                                                        return;
                                                    const { css, js } = await api.getPageAssets(slug);
                                                    setPageCode({ css, js });
                                                }, title: "Page-level CSS / JS", children: '{ }' }), _jsx("button", { className: "ghost", onClick: handleUndo, disabled: busyMutation, title: "Undo (Ctrl+Z)", children: "\u21BA" }), _jsx("button", { className: "ghost", onClick: handleRedo, disabled: busyMutation, title: "Redo (Ctrl+Shift+Z)", children: "\u21BB" }), _jsx("button", { className: "ghost", onClick: handleDiscard, disabled: !dirty || busySave, title: "Discard unsaved changes", children: "Discard" }), _jsx("button", { onClick: handleSave, disabled: !dirty || busySave, title: "Save (Ctrl+S)", children: busySave ? 'Saving…' : 'Save' }), _jsxs("button", { className: "rail-toggle", onClick: toggleInspector, title: inspectorOpen ? 'Hide inspector' : 'Show inspector', "aria-pressed": inspectorOpen, children: ["Inspector ", inspectorOpen ? '▶' : '◀'] })] })] }), conflict && (_jsxs("div", { className: "conflict-bar pad", children: [_jsx("strong", { children: "Conflict:" }), " Someone saved this page after you started editing.", _jsx("button", { className: "ghost", onClick: () => {
                                            setSavedPage(conflict.page);
                                            setDraftPage(conflict.page);
                                            setSavedContent(conflict.content_map);
                                            setDraftContent({});
                                            setTombedIds([]);
                                            setEtag(conflict.etag);
                                            setConflict(null);
                                            setError(null);
                                            previewHandle.current?.reload();
                                        }, children: "Use their version (discard mine)" }), _jsx("button", { onClick: () => {
                                            // Fast-forward etag and retry the save with the user's draft.
                                            setSavedContent(conflict.content_map);
                                            setEtag(conflict.etag);
                                            setConflict(null);
                                            setError(null);
                                            handleSave();
                                        }, children: "Overwrite with mine" })] })), _jsxs("div", { className: workspaceCls, children: [treeOpen && (_jsxs("aside", { className: "editor-tree", children: [_jsx("div", { className: "rail-head", children: _jsx("span", { children: "Block tree" }) }), _jsx("div", { className: "tree-scroll", children: _jsx(BlockTree, { blocks: draftPage.blocks, selectedId: selectedId, onSelect: setSelectedId, onReorder: handleReorder, onDuplicate: handleDuplicate, onDelete: handleDelete, onAddChild: handleAddChild }) })] })), _jsxs("main", { className: "editor-canvas", ref: previewRef, children: [_jsx(Preview, { ref: previewHandle, publicBase: config.public_url, slug: slug, reloadKey: previewKey, selectedId: selectedId, onClickInIframe: setSelectedId, editMode: editMode, onEditModeChange: (next) => { setEditMode(next); setInlineEditingBlockId(null); }, onInlineMessage: handleInlineMessage }), inlineToast && _jsx("div", { className: "inline-toast", children: inlineToast })] }), inspectorOpen && (_jsx("aside", { className: "editor-inspector", children: selected ? (_jsx(InspectorTabs, { slug: slug, block: selected, inlineEditingBlockId: inlineEditingBlockId, onSettingsChange: (s) => handleSettingsChange(selected.id, s), contentValue: selected.content ? (contentFor(selected.id) ?? '') : undefined, onContentChange: selected.content ? (html) => handleContentChange(selected.id, html) : undefined, onContentSaved: () => {
                                                // AI tab still posts directly to disk; refresh saved+draft
                                                // for that block so the editor stays consistent.
                                                if (!slug)
                                                    return;
                                                api.getContent(slug, selected.id).then(({ html }) => {
                                                    setSavedContent((m) => ({ ...m, [selected.id]: html }));
                                                    setDraftContent((m) => { const next = { ...m }; delete next[selected.id]; return next; });
                                                    scheduleBlockPatch(selected.id);
                                                }).catch(() => { });
                                            }, onOpenCustomCode: () => setBlockCode({
                                                block: selected,
                                                css: typeof selected.settings.custom_css === 'string' ? selected.settings.custom_css : '',
                                                js: typeof selected.settings.custom_js === 'string' ? selected.settings.custom_js : '',
                                            }), forceTab: forceInspectorTab && forceInspectorTab.blockId === selected.id ? forceInspectorTab : null }, selected.id)) : (_jsx("div", { className: "ins-empty muted small pad", children: "Pick a block from the tree to edit its settings, content, or run AI tools on it." })) }))] })] }))] }), pageCode && slug && (_jsx(CodePanelLazy, { title: `Page code · /${slug === 'home' ? '' : slug}`, subtitle: "Loaded only on this page, after site-wide code.", scopeHint: `/* assets/pages/${slug}.{css,js} */`, brand: brand, initial: pageCode, onSave: async (next) => {
                    await api.putPageAssets(slug, next);
                    setPageCode(null);
                    previewHandle.current?.reload();
                }, onClose: () => setPageCode(null) })), blockCode && slug && (_jsx(CodePanelLazy, { title: `Block code · ${blockCode.block.type}`, subtitle: `Scoped to this block. CSS prefixed automatically; JS receives \`block\` element.`, scopeHint: `[data-block-id="${blockCode.block.id}"]`, brand: brand, initial: { css: blockCode.css, js: blockCode.js }, onSave: async (next) => {
                    // Custom CSS/JS go into the block's settings via the draft.
                    const newSettings = { ...(blockCode.block.settings ?? {}) };
                    if (next.css)
                        newSettings.custom_css = next.css;
                    else
                        delete newSettings.custom_css;
                    if (next.js)
                        newSettings.custom_js = next.js;
                    else
                        delete newSettings.custom_js;
                    handleSettingsChange(blockCode.block.id, newSettings);
                    setBlockCode(null);
                }, onClose: () => setBlockCode(null) })), mediaPicker && (_jsx(MediaBrowser, { accept: mediaPicker.kind === 'video' ? 'video' : 'image', onPick: handleMediaPickFromIframe, onClose: () => setMediaPicker(null) })), showPageSettings && draftPage && slug && (_jsx(PageSettingsModal, { page: draftPage, onSaved: (saved, finalSlug) => {
                    setShowPageSettings(false);
                    setPagesRefreshKey((k) => k + 1);
                    setSavedPage(saved);
                    setDraftPage(saved);
                    previewHandle.current?.reload();
                    if (finalSlug !== slug)
                        navigate(`/pages/${finalSlug}`);
                }, onClose: () => setShowPageSettings(false) }))] }));
}
function EmptyHint() {
    return (_jsxs("div", { className: "empty-hint pad", children: [_jsx("h3", { children: "Pick a page" }), _jsx("p", { className: "muted", children: "Select a page from the left to load its block tree and live preview." })] }));
}
// ============================================================
// Tree mutation helpers (operate on draft tree only)
// ============================================================
const WRAPPER_TYPES = new Set(['section', 'container', 'row', 'column']);
function makeNewBlock(type, layout, usedIds, settings) {
    const id = freshId(type, usedIds);
    const isWrapper = WRAPPER_TYPES.has(type);
    return {
        id,
        type,
        content: isWrapper ? '' : `${id}.html`,
        layout: layout ?? null,
        settings: settings ?? {},
        children: isWrapper ? [] : [],
    };
}
function freshId(type, usedIds) {
    const slug = type.replace(/[^a-z0-9]+/g, '-').toLowerCase() || 'block';
    const used = new Set(usedIds);
    for (let i = 0; i < 50; i++) {
        const id = `${slug}-${randomHex(5)}`;
        if (!used.has(id))
            return id;
    }
    return `${slug}-${Date.now().toString(36)}`;
}
function randomHex(n) {
    let s = '';
    while (s.length < n)
        s += Math.floor(Math.random() * 16).toString(16);
    return s.slice(0, n);
}
function collectIds(blocks) {
    const out = [];
    for (const b of blocks) {
        out.push(b.id);
        if (b.children.length)
            out.push(...collectIds(b.children));
    }
    return out;
}
function insertInTree(blocks, parentId, newBlock) {
    if (parentId === null)
        return { blocks: [...blocks, newBlock], parent: null };
    const next = mapTree(blocks, (b) => {
        if (b.id !== parentId)
            return b;
        return { ...b, children: [...b.children, newBlock] };
    });
    return { blocks: next, parent: parentId };
}
function insertAtIndex(blocks, parentId, idx, newBlock) {
    if (parentId === null) {
        return [...blocks.slice(0, idx), newBlock, ...blocks.slice(idx)];
    }
    return mapTree(blocks, (b) => {
        if (b.id !== parentId)
            return b;
        return { ...b, children: [...b.children.slice(0, idx), newBlock, ...b.children.slice(idx)] };
    });
}
function updateBlockInTree(blocks, id, fn) {
    return blocks.map((b) => {
        if (b.id === id)
            return fn(b);
        if (b.children.length) {
            const next = updateBlockInTree(b.children, id, fn);
            if (next !== b.children)
                return { ...b, children: next };
        }
        return b;
    });
}
function removeFromTree(blocks, id) {
    const out = [];
    for (const b of blocks) {
        if (b.id === id)
            continue;
        if (b.children.length)
            out.push({ ...b, children: removeFromTree(b.children, id) });
        else
            out.push(b);
    }
    return out;
}
function cloneSubtreeWithFreshIds(block, usedIds, oldToNew) {
    const newId = freshId(block.type, usedIds);
    usedIds.push(newId);
    oldToNew[block.id] = newId;
    return {
        ...block,
        id: newId,
        content: block.content ? `${newId}.html` : '',
        children: block.children.map((c) => cloneSubtreeWithFreshIds(c, usedIds, oldToNew)),
    };
}
function findParentId(blocks, id, parent = null) {
    for (const b of blocks) {
        if (b.id === id)
            return parent;
        if (b.children.length) {
            const inner = findParentId(b.children, id, b.id);
            if (inner !== null)
                return inner;
        }
        // Also handle case where the block is at root and parent stays null:
        // we only return null if we also confirmed no deeper match.
    }
    // Final pass: confirm id exists at root.
    for (const b of blocks)
        if (b.id === id)
            return null;
    return null;
}
function indexOf(blocks, id, parentId) {
    if (parentId === null) {
        for (let i = 0; i < blocks.length; i++)
            if (blocks[i].id === id)
                return i;
        return -1;
    }
    const parent = findBlock(blocks, parentId);
    if (!parent)
        return -1;
    for (let i = 0; i < parent.children.length; i++)
        if (parent.children[i].id === id)
            return i;
    return -1;
}
function nextSiblingId(blocks, parentId, id) {
    const arr = parentId === null ? blocks : (findBlock(blocks, parentId)?.children ?? []);
    const idx = arr.findIndex((b) => b.id === id);
    if (idx < 0 || idx >= arr.length - 1)
        return null;
    return arr[idx + 1].id;
}
function applyTreeDrop(blocks, srcId, overId, position) {
    if (srcId === overId)
        return null;
    const path = findAncestorPath(blocks, overId);
    if (!path || path.length < 1)
        return null;
    const targetParent = path.length >= 2 ? path[path.length - 2].id : '__root__';
    const src = findBlock(blocks, srcId);
    if (!src)
        return null;
    if (containsId(src, overId))
        return null;
    const { remaining, removed } = detach(blocks, srcId);
    if (!removed)
        return null;
    if (targetParent === '__root__') {
        const idx = remaining.findIndex((b) => b.id === overId);
        if (idx < 0)
            return null;
        const ins = position === 'before' ? idx : idx + 1;
        return [...remaining.slice(0, ins), removed, ...remaining.slice(ins)];
    }
    let mutated = false;
    const next = mapTree(remaining, (b) => {
        if (mutated)
            return b;
        if (b.id !== targetParent)
            return b;
        const idx = b.children.findIndex((c) => c.id === overId);
        if (idx < 0)
            return b;
        const ins = position === 'before' ? idx : idx + 1;
        mutated = true;
        return { ...b, children: [...b.children.slice(0, ins), removed, ...b.children.slice(ins)] };
    });
    return mutated ? next : null;
}
function detach(blocks, id) {
    const next = [];
    let removed = null;
    for (const b of blocks) {
        if (b.id === id) {
            removed = b;
            continue;
        }
        if (b.children.length) {
            const inner = detach(b.children, id);
            if (inner.removed) {
                removed = inner.removed;
                next.push({ ...b, children: inner.remaining });
                continue;
            }
        }
        next.push(b);
    }
    return { remaining: next, removed };
}
function mapTree(blocks, fn) {
    return blocks.map((b) => {
        const mapped = fn(b);
        if (mapped.children.length) {
            const inner = mapTree(mapped.children, fn);
            if (inner !== mapped.children)
                return { ...mapped, children: inner };
        }
        return mapped;
    });
}
function containsId(block, id) {
    if (block.id === id)
        return true;
    for (const c of block.children)
        if (containsId(c, id))
            return true;
    return false;
}
function findAncestorPath(blocks, id, trail = []) {
    for (const b of blocks) {
        const next = [...trail, b];
        if (b.id === id)
            return next;
        if (b.children.length) {
            const inner = findAncestorPath(b.children, id, next);
            if (inner)
                return inner;
        }
    }
    return null;
}
function loadRail(key, fallback) {
    try {
        const v = localStorage.getItem(key);
        if (v === null)
            return fallback;
        return v === '1';
    }
    catch {
        return fallback;
    }
}
function saveRail(key, value) {
    try {
        localStorage.setItem(key, value ? '1' : '0');
    }
    catch { /* noop */ }
}
function findBlock(blocks, id) {
    for (const b of blocks) {
        if (b.id === id)
            return b;
        if (b.children.length) {
            const inner = findBlock(b.children, id);
            if (inner)
                return inner;
        }
    }
    return null;
}
function flatten(blocks) {
    const out = [];
    const walk = (bs) => { for (const b of bs) {
        out.push(b);
        if (b.children.length)
            walk(b.children);
    } };
    walk(blocks);
    return out;
}
// ---------- inline-edit helpers (preserved from previous Editor) ----------
function applyTextEditToSource(source, opts) {
    const { tag, ordinal, originalText, newInnerHtml } = opts;
    const tagLower = tag.toLowerCase();
    let doc;
    try {
        doc = new DOMParser().parseFromString(`<div id="__root">${source}</div>`, 'text/html');
    }
    catch {
        return { html: source, located: false };
    }
    const root = doc.getElementById('__root');
    if (!root)
        return { html: source, located: false };
    const candidates = Array.from(root.getElementsByTagName(tagLower));
    if (candidates.length === 0)
        return { html: source, located: false };
    const norm = (s) => s.replace(/\s+/g, ' ').trim();
    const wantText = norm(originalText);
    let target = candidates[ordinal] ?? null;
    if (!target || norm(target.textContent || '') !== wantText) {
        target = candidates.find((c) => norm(c.textContent || '') === wantText) ?? null;
    }
    if (!target)
        return { html: source, located: false };
    target.innerHTML = newInnerHtml;
    return { html: root.innerHTML, located: true };
}
function applyMediaSwapToSource(source, ordinal, newRel) {
    const slotRe = /\{\{(picture|img|video):[^}]*\}\}|<img\b[^>]*\/?\s*>/gi;
    const slots = [];
    let m;
    while ((m = slotRe.exec(source)) !== null) {
        slots.push({ start: m.index, end: m.index + m[0].length, raw: m[0], isToken: m[0].startsWith('{{') });
    }
    if (!Number.isInteger(ordinal) || ordinal < 0 || ordinal >= slots.length)
        return { html: source, located: false };
    const slot = slots[ordinal];
    let replacement = slot.raw;
    if (slot.isToken) {
        replacement = slot.raw.replace(/\{\{(picture|img|video):[^|}]+/i, (_, kind) => `{{${kind}:${newRel}`);
    }
    else {
        replacement = slot.raw.replace(/(\bsrc\s*=\s*)(['"])[^'"]+\2/i, (_m, eq, q) => `${eq}${q}${newRel}${q}`);
    }
    if (replacement === slot.raw)
        return { html: source, located: true };
    return { html: source.slice(0, slot.start) + replacement + source.slice(slot.end), located: true };
}
function sanitizeLeafHtml(html) {
    return html
        .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<\/?font\b[^>]*>/gi, '')
        .trim();
}
function resolveSmartParent(blocks, selectedId, type) {
    if (type === 'section')
        return null;
    const wanted = type === 'container' ? 'section'
        : type === 'row' ? 'container'
            : type === 'column' ? 'row'
                : 'column';
    if (!selectedId)
        return null;
    const path = findAncestorPath(blocks, selectedId);
    if (!path)
        return null;
    for (let i = path.length - 1; i >= 0; i--) {
        if (path[i].type === wanted)
            return path[i].id;
    }
    return null;
}
